import {
  blockedDepositAccount,
  getMetadataFromAccount,
  isPammInvestorAccount,
} from '../../../common/utils/accounts'

type PaymentOption = {
  provider: string
}

export function isDepositPaymentOption({provider}: PaymentOption): boolean {
  return provider === 'enovipayCoinspaid'
}

/**
 * The linked trading account id a PAMM investor account's deposits must target
 * (PT-5568) — the account shown as "Linked trading account" on the PAMM card.
 * Read from `metadata.relatedForexAccountId`.
 *
 * @returns The linked account id, or null when none is configured.
 */
export const getLinkedTradingAccountId = (pammAccount: any): number | null => {
  const linkedId = getMetadataFromAccount(pammAccount).relatedForexAccountId
  return linkedId != null && linkedId !== '' ? Number(linkedId) : null
}

/**
 * Accounts shown in the deposit "Choose account" selector (PT-5568).
 *
 * Depositable accounts come first, then the linked trading accounts that PAMM
 * investments deposit into, then the PAMM investor accounts themselves. The
 * linked trading accounts (PAMM follower accounts) and PAMM investor accounts
 * are otherwise filtered out of the deposit list, but both are surfaced here so
 * the client can deposit into the linked account and be guided from the PAMM
 * account. They are appended after the plain depositable accounts so one of
 * those is preferred as the default selection when the client has any.
 *
 * @param accounts All of the client's accounts.
 * @param includeDemo Whether demo accounts should be listed (true only when a
 *   demo account is the active selection).
 */
export const buildDepositAccountList = (
  accounts: any[],
  {includeDemo}: {includeDemo: boolean},
): any[] => {
  const depositableAccounts = accounts.filter(
    (account) => (includeDemo || !account.isDemo) && !blockedDepositAccount(account),
  )
  const pammInvestorAccounts = accounts.filter(
    (account) => !account.isDemo && !account.isArchived && isPammInvestorAccount(account),
  )
  // The specific trading accounts PAMM investments deposit into (relatedForexAccountId).
  const linkedAccountIds = new Set(
    pammInvestorAccounts
      .map(getLinkedTradingAccountId)
      .filter((id): id is number => id != null),
  )
  const alreadyListed = new Set(depositableAccounts.map((account) => account.id))
  const linkedTradingAccounts = accounts.filter(
    (account) =>
      !account.isDemo &&
      !account.isArchived &&
      linkedAccountIds.has(account.id) &&
      !alreadyListed.has(account.id),
  )
  return [...depositableAccounts, ...linkedTradingAccounts, ...pammInvestorAccounts]
}

/**
 * First depositable account from a deposit account list (PT-5568) — i.e. the
 * first that is not a PAMM investor account. Used as a fallback to move the
 * deposit flow off a PAMM investor account when no linked trading account is
 * resolvable.
 *
 * @returns The first depositable account, or undefined if there is none.
 */
export const findFirstDepositableAccount = (accounts: any[]): any =>
  accounts.find((account) => !isPammInvestorAccount(account))
